import { ref } from "vue";
import { useToast } from "@eudonet/opal";
import { useI18n } from "vue-i18n";
import { resolveApiErrorMessage } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { listConsentPurposes, createConsentPurpose, updateConsentPurpose, setConsentPurposeActive, deleteConsentPurpose, } from "../infrastructure/adapters/consentPurposeService";
/**
 * R70: backend `errorCode` -> i18n key. Covers every code the catalogue
 * validators (`ConsentPurpose.*`, dotted PascalCase) and the exception handlers
 * (`PURPOSE_*`, SCREAMING_SNAKE) can emit. Keep in sync with the OPINK
 * ConsentPurpose validators + IExceptionHandlers.
 */
const ERROR_OVERRIDES = {
    "ConsentPurpose.Code.Required": "consentPurposeCatalogue.errors.codeRequired",
    "ConsentPurpose.Code.Invalid": "consentPurposeCatalogue.errors.codeInvalid",
    "ConsentPurpose.Translations.Required": "consentPurposeCatalogue.errors.translationsRequired",
    "ConsentPurpose.Locale.Invalid": "consentPurposeCatalogue.errors.localeInvalid",
    "ConsentPurpose.Label.Required": "consentPurposeCatalogue.errors.labelRequired",
    "ConsentPurpose.Label.TooLong": "consentPurposeCatalogue.errors.labelTooLong",
    "ConsentPurpose.Description.TooLong": "consentPurposeCatalogue.errors.descriptionTooLong",
    PURPOSE_IN_USE: "consentPurposeCatalogue.errors.purposeInUse",
    PURPOSE_NOT_FOUND: "consentPurposeCatalogue.errors.purposeNotFound",
    PURPOSE_ALREADY_EXISTS: "consentPurposeCatalogue.errors.purposeAlreadyExists",
};
const FALLBACK_KEY = "consentPurposeCatalogue.errors.generic";
export function useConsentPurposeCatalogue() {
    const purposes = ref([]);
    const isLoading = ref(false);
    const loadError = ref(false);
    // Static success messages go through useAppToast (i18n KEY in, t() applied).
    const { toastSuccess } = useAppToast();
    // Error toasts carry an ALREADY-RESOLVED string from resolveApiErrorMessage,
    // so they must NOT go through useAppToast.toastError (which would re-run t()
    // on the resolved text). Mirror SendTestEmailModal: showToast({ message }).
    const { showToast } = useToast();
    const { t } = useI18n();
    /** Surfaces an already-resolved error message in a toast without re-translating it. */
    function toastResolvedError(message) {
        showToast({
            message,
            ariaClose: t("form.dismissToast"),
            status: "error",
        });
    }
    async function load() {
        isLoading.value = true;
        loadError.value = false;
        try {
            purposes.value = await listConsentPurposes();
        }
        catch (err) {
            logger.error("Failed to load consent purposes", err);
            loadError.value = true;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function create(body) {
        try {
            await createConsentPurpose(body);
            toastSuccess("consentPurposeCatalogue.createSuccess");
            await load();
            return true;
        }
        catch (err) {
            const message = resolveApiErrorMessage(err, ERROR_OVERRIDES, FALLBACK_KEY);
            toastResolvedError(message);
            logger.error("Failed to create consent purpose", err);
            return false;
        }
    }
    async function update(code, body) {
        try {
            await updateConsentPurpose(code, body);
            toastSuccess("consentPurposeCatalogue.updateSuccess");
            await load();
            return true;
        }
        catch (err) {
            const message = resolveApiErrorMessage(err, ERROR_OVERRIDES, FALLBACK_KEY);
            toastResolvedError(message);
            logger.error("Failed to update consent purpose", err);
            return false;
        }
    }
    async function toggleActive(code, isActive) {
        try {
            await setConsentPurposeActive(code, isActive);
            await load();
        }
        catch (err) {
            const message = resolveApiErrorMessage(err, ERROR_OVERRIDES, FALLBACK_KEY);
            toastResolvedError(message);
            logger.error("Failed to toggle consent purpose", err);
        }
    }
    async function remove(code) {
        try {
            await deleteConsentPurpose(code);
            toastSuccess("consentPurposeCatalogue.deleteSuccess");
            await load();
            return true;
        }
        catch (err) {
            const message = resolveApiErrorMessage(err, ERROR_OVERRIDES, FALLBACK_KEY);
            toastResolvedError(message);
            logger.error("Failed to delete consent purpose", err);
            return false;
        }
    }
    return {
        purposes,
        isLoading,
        loadError,
        load,
        create,
        update,
        toggleActive,
        remove,
    };
}
